export const PRODUCTS_TREE_VIEW_PAGE_SIZE = 5000

export function menuHasChildren(menuId, menuItems, products) {
	const hasSubmenus = (menuItems || []).some((menuItem) => menuItem.parent_id === menuId)
	const hasProducts = (products || []).some((product) => Number(product.menu_id) === Number(menuId))
	return hasSubmenus || hasProducts
}

/**
 * Counts products in each menu including nested submenus.
 * Cycle-safe: broken parent_id chains (A→B→A / self-parent) won't crash the page.
 */
export function buildMenuProductCounts(menuItems, products, options = {}) {
	const { includeWithoutMenu = false, withoutMenuKey = 'menu-none' } = options
	const childrenByParent = new Map()

	for (const item of menuItems || []) {
		const parentKey = item.parent_id == null ? null : Number(item.parent_id)
		const childId = Number(item.id)
		if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, [])
		childrenByParent.get(parentKey).push(childId)
	}

	const directCounts = new Map()
	let withoutMenuCount = 0
	for (const product of products || []) {
		if (!product.menu_id) {
			withoutMenuCount += 1
			continue
		}
		const menuId = Number(product.menu_id)
		directCounts.set(menuId, (directCounts.get(menuId) || 0) + 1)
	}

	const memo = new Map()
	const countProducts = (menuId, stack = new Set()) => {
		if (memo.has(menuId)) return memo.get(menuId)
		if (stack.has(menuId)) return 0

		stack.add(menuId)
		const total = (directCounts.get(menuId) || 0) +
			(childrenByParent.get(menuId) || []).reduce(
				(sum, childId) => sum + countProducts(childId, stack),
				0
			)
		stack.delete(menuId)
		memo.set(menuId, total)
		return total
	}

	const counts = new Map()
	for (const item of menuItems || []) {
		counts.set(item.id, countProducts(Number(item.id)))
	}
	if (includeWithoutMenu) {
		counts.set(withoutMenuKey, withoutMenuCount)
	}
	return counts
}

export function buildMenuProductTreeRows(menuItems, products, expandedMenuIds, withoutMenuLabel) {
	const menuList = menuItems || []
	const productList = products || []
	const expanded = expandedMenuIds || new Set()
	const rows = []

	const walkMenus = (parentId, depth, ancestors = new Set()) => {
		menuList
			.filter((menuItem) => menuItem.parent_id === parentId)
			.forEach((menuItem) => {
				const menuId = Number(menuItem.id)
				if (ancestors.has(menuId)) {
					return
				}

				const collapseKey = menuItem.id
				const hasChildren = menuHasChildren(menuItem.id, menuList, productList)
				const isExpanded = expanded.has(collapseKey)

				rows.push({
					rowType: 'menu',
					key: `menu-${menuItem.id}`,
					menu: menuItem,
					depth,
					hasChildren,
					isCollapsed: !isExpanded,
					collapseKey,
				})

				if (!isExpanded) {
					return
				}

				productList
					.filter((product) => Number(product.menu_id) === Number(menuItem.id))
					.forEach((product) => {
						rows.push({
							rowType: 'product',
							key: `product-${product.id}`,
							product,
							depth: depth + 1,
						})
					})

				const nextAncestors = new Set(ancestors)
				nextAncestors.add(menuId)
				walkMenus(menuItem.id, depth + 1, nextAncestors)
			})
	}

	walkMenus(null, 0)

	const productsWithoutMenu = productList.filter((product) => !product.menu_id)
	if (productsWithoutMenu.length > 0) {
		const collapseKey = 'menu-none'
		const isExpanded = expanded.has(collapseKey)

		rows.push({
			rowType: 'menu',
			key: collapseKey,
			menu: { id: collapseKey, name: withoutMenuLabel },
			depth: 0,
			hasChildren: true,
			isCollapsed: !isExpanded,
			collapseKey,
			isVirtual: true,
		})

		if (isExpanded) {
			productsWithoutMenu.forEach((product) => {
				rows.push({
					rowType: 'product',
					key: `product-${product.id}`,
					product,
					depth: 1,
				})
			})
		}
	}

	return rows
}
